import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  HStack,
  Select,
  VStack,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import FormikSelect from "../../common/PopUp/ChakraFormik/FormikSelect";
import { useFormik } from "formik";
import uniqid from "uniqid";
import color from "../../../config/color";
import { fetchWithToken } from "../../../functions/fetch";
import { downloadFile } from "../../../functions/file";
import { useQuery } from "@tanstack/react-query";
import {
  createReportSchema,
  createReportValues,
} from "../../../validation/reportValidation";
import ErrorMessages from "../../common/PopUp/ChakraFormik/ErrorMessages";

const ReportTab = () => {
  const { feeTypeValue, leaseTypeValue, allUserValue, allMonthValue } =
    createReportValues;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      type: "",
      user: "",
      year: "",
      month: "",
    },
    onSubmit: async (values) => {
      const { type, year, month, user: userId } = values;
      try {
        setIsSubmitting(true);
        let url = "";
        const isAnnual = month === allMonthValue;
        // request csv string
        if (type === feeTypeValue) {
          url = `${process.env.REACT_APP_BACKEND_HOST}/api/report/fee`;
          if (isAnnual) {
            url += `/annual?year=${year}&userId=${userId}`;
          } else {
            url += `/monthly?year=${year}&month=${month}`;
          }
        } else if (type === leaseTypeValue) {
          url = `${process.env.REACT_APP_BACKEND_HOST}/api/report/lease?year=${year}${isAnnual ? "" : `&month=${month}`}`;
        } else {
          console.error(`Invalid type:${type}`);
          setIsSubmitting(false);
          return;
        }

        const res = await fetchWithToken("GET", url);
        if (res.status === 400) {
          console.error(res);
          setIsSubmitting(false);
          return;
        }

        const { csvString } = res;

        // file name
        let fileName = "";
        if (type === leaseTypeValue) {
          fileName = `房間租約報表_${year}`;
          if (!isAnnual) {
            fileName += `_${month.padStart(2, "0")}`;
          }
        } else if (type === feeTypeValue) {
          fileName = `住戶費用報表_${year}`;
          if (isAnnual) {
            // add user info
            const user = users.find((renter) => renter._id === userId);
            if (!user) {
              console.error(`User with id not found: ${userId}`);
              setIsSubmitting(false);
              return;
            } else {
              fileName += `_${user.name}_${user.memberNumber}`;
            }
          } else {
            // add month
            fileName += `_${month.padStart(2, "0")}`;
          }
        }

        downloadFile({
          data: csvString,
          fileName,
          fileType: "text/csv",
        });
      } catch (error) {
        console.error(error);
      }
      setIsSubmitting(false);
    },
    validationSchema: createReportSchema,
  });

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () =>
      (
        await fetchWithToken(
          "GET",
          `${process.env.REACT_APP_BACKEND_HOST}/api/user`,
        )
      ).users,
    initialData: [],
  });

  const { type, user } = formik.values;

  // when type changed, clear other values
  useEffect(() => {
    formik.setFieldValue("user", "");
    formik.setFieldValue("year", "");
    formik.setFieldValue("month", "");
  }, [type]);

  // when user changed, clear year and month(fee report with single user require month)

  const startYear = 2023; // the first selectable year in option
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i,
  );

  return (
    <VStack mt={8} ml={12}>
      <FormikSelect
        formik={formik}
        field="type"
        labelText="報表類別"
        placeholder="選擇類別"
      >
        <option value={feeTypeValue}>費用</option>
        <option value={leaseTypeValue}>租約</option>
      </FormikSelect>
      <FormikSelect
        formik={formik}
        field="user"
        labelText="報表對象"
        placeholder="選擇對象"
      >
        <option value={allUserValue}>全部住戶</option>
        {
          // allow user option when exporting fee report
          type === feeTypeValue &&
            users.map((renter) => (
              <option
                key={uniqid()}
                value={renter._id}
              >{`${renter.name}, 會員編號：${renter.memberNumber}`}</option>
            ))
        }
      </FormikSelect>
      <FormControl>
        <HStack>
          <FormLabel whiteSpace="nowrap">報表區間</FormLabel>
          <Select
            variant="filled"
            w="fit-content"
            {...formik.getFieldProps("year")}
            placeholder="年"
          >
            {years.map((year) => (
              <option value={year} key={uniqid()}>
                {year}
              </option>
            ))}
          </Select>
          <Select
            variant="filled"
            w="fit-content"
            {...formik.getFieldProps("month")}
            placeholder="月"
          >
            {
              // not allow single user fee report
              !(type === feeTypeValue && user !== allUserValue) &&
                Array(12)
                  .fill(0)
                  .map((_, index) => (
                    <option
                      value={index + 1}
                      key={uniqid()}
                    >{`${index + 1}月`}</option>
                  ))
            }
            {
              // not allow fee report of every room
              !(type === feeTypeValue && user === allUserValue) && (
                <option value={allMonthValue}>所有月份</option>
              )
            }
          </Select>
        </HStack>
      </FormControl>

      <ButtonGroup mt={2}>
        <Button
          isLoading={isSubmitting}
          borderRadius="full"
          colorScheme="orange"
          bgColor={color.orange}
          onClick={formik.handleSubmit}
        >
          下載報表
        </Button>
        <Button
          isLoading={isSubmitting}
          borderRadius="full"
          bgColor={color.gray}
          color="white"
          onClick={() => formik.resetForm()}
        >
          取消重設
        </Button>
      </ButtonGroup>
      <ErrorMessages formik={formik} />
    </VStack>
  );
};

export default ReportTab;
